import {
  CabinetSupport,
  CrossPieceType,
  Material,
  MAX_WIDTH_ACCEPT_FRONT_EP,
  Position,
} from '../constants';
import { Top } from '../entities/Top';
import { Rack } from '../entities/Rack';
import { Shelf } from '../entities/Shelf';
import { Cabinet } from '../entities/Cabinet';

enum CrossPiecesProperties {
  Width = 'Width',
  Material = 'Material',
  Type = 'Type',
  Reinforce = 'Reinforce',
  Rod = 'Rod',
}

function canRemoveCrossPieceShelf(
  shelf: Shelf,
  rack: Rack,
  position: Position,
): boolean {
  if (position === Position.Left || position === Position.Right) {
    if (
      rack.plate &&
      (!rack.getCrossPiece(Position.Front) ||
        !rack.getCrossPiece(Position.Back))
    )
      return false;

    if (shelf.getBelowShelf()?.rod) return false;
    if (rack.hasPaneForPosition(position)) return false;
  } else if (position === Position.Front || position === Position.Back) {
    if (rack.level > 0 && shelf.hasDoorForPosition(position)) return true;

    if (
      rack.plate &&
      (!rack.getCrossPiece(Position.Left) ||
        !rack.getCrossPiece(Position.Right))
    )
      return false;
    if (shelf.getBelowShelf()?.hasDoorForPosition(position)) return false;
    if (shelf.hasDoorForPosition(position) || rack.hasPaneForPosition(position))
      return false;
  }

  return true;
}

function canRemoveCrossPieceTop(lastShelf: Shelf, position: Position): boolean {
  const top = lastShelf.cabinet.top;
  if (position === Position.Left || position === Position.Right) {
    if (lastShelf.rod) return false;
    if (
      top.plate &&
      (!top.getCrossPiece(Position.Front) || !top.getCrossPiece(Position.Back))
    )
      return false;
  } else if (position === Position.Front || position === Position.Back) {
    if (lastShelf.hasDoorForPosition(position)) return false;
    if (
      top.plate &&
      (!top.getCrossPiece(Position.Left) || !top.getCrossPiece(Position.Right))
    )
      return false;
  }

  return true;
}

function addAcceptedCrossPiecesWhenEmptyAbove(
  types: (CrossPieceType | undefined)[],
  belowShelf: Shelf | undefined,
  position: Position.Front | Position.Back,
  cabinetWidth: number,
  rack?: Rack,
): (CrossPieceType | undefined)[] {
  // ED always accepted for first rack and top
  if (!rack || rack.level === 0)
    types.push(CrossPieceType.UpperEmptyLowerDoors);

  // EP only accepted in back position when no doors in below shelf,
  // and for front, require low width too because at these low widths,
  // plates in stock are depth reduced
  if (
    (position === Position.Back ||
      (position === Position.Front &&
        cabinetWidth <= MAX_WIDTH_ACCEPT_FRONT_EP)) &&
    ((rack && rack.level > 0) || !belowShelf?.hasDoorForPosition(position))
  )
    types.push(CrossPieceType.UpperEmptyLowerPane);

  return types;
}

export function acceptedCrossPieceTypesForShelf(
  shelf: Shelf,
  rack: Rack,
  position: Position,
): (CrossPieceType | undefined)[] {
  /*
    Get array of accepted types of cross pieces for a given shelf and position
    If undefined is present, accept no cross piece for this shelf position
    */
  let types: (CrossPieceType | undefined)[] = [];

  if (position === Position.Left || position === Position.Right) {
    // PP always accepted in lateral positions
    types.push(CrossPieceType.UpperPaneLowerPane);

    // EP only accepted in lateral positions when no pane above
    if (!rack.hasPaneForPosition(position))
      types.push(CrossPieceType.UpperEmptyLowerPane);

    // DD/ED never accepted in lateral positions because no doors exist for these positions
  } else if (position === Position.Front || position === Position.Back) {
    // Never have a cross piece on front for racks above the firt rack
    // No cross piece on back for racks above the firt rack if back door
    if (
      rack.level > 0 &&
      (position === Position.Front ||
        (position === Position.Back && shelf.hasDoorForPosition(position)))
    )
      return [undefined];

    // DD always accepted in front and back positions for first rack
    if (rack.level === 0) types.push(CrossPieceType.UpperDoorsLowerDoors);

    // PP restriction in front, in goal to avoid reduced depth plates
    if (
      ((position === Position.Front &&
        shelf.cabinet.width <= MAX_WIDTH_ACCEPT_FRONT_EP) ||
        position === Position.Back) &&
      !shelf.hasDoorForPosition(position) &&
      (rack.level > 0 || !shelf.getBelowShelf()?.hasDoorForPosition(position))
    )
      types.push(CrossPieceType.UpperPaneLowerPane);

    // EP/ED require empty above
    if (
      !shelf.hasDoorForPosition(position) &&
      !rack.hasPaneForPosition(position)
    )
      types = addAcceptedCrossPiecesWhenEmptyAbove(
        types,
        shelf.getBelowShelf(),
        position,
        shelf.cabinet.width,
        rack,
      );
  }

  if (canRemoveCrossPieceShelf(shelf, rack, position)) types.push(undefined);

  return types;
}

export function acceptedCrossPieceTypesForTop(
  lastShelf: Shelf,
  position: Position,
): (CrossPieceType | undefined)[] {
  /*
    Get array of accepted types of cross pieces for the cabinet top and position
    If undefined is present, accept no cross piece for this top position
    */
  let types: (CrossPieceType | undefined)[] = [];

  if (position === Position.Left || position === Position.Right) {
    // EP alone accepted for lateral positions in top cabinet
    types.push(CrossPieceType.UpperEmptyLowerPane);
  } else if (position === Position.Front || position === Position.Back)
    types = addAcceptedCrossPiecesWhenEmptyAbove(
      types,
      lastShelf,
      position,
      lastShelf.cabinet.width,
    );

  if (canRemoveCrossPieceTop(lastShelf, position)) types.push(undefined);

  return types;
}

export function getDefaultCrossPieceType(
  types: (CrossPieceType | undefined)[],
): CrossPieceType {
  if (types.includes(CrossPieceType.UpperEmptyLowerPane))
    return CrossPieceType.UpperEmptyLowerPane;
  if (types.includes(CrossPieceType.UpperEmptyLowerDoors))
    return CrossPieceType.UpperEmptyLowerDoors;
  if (types.includes(CrossPieceType.UpperPaneLowerPane))
    return CrossPieceType.UpperPaneLowerPane;
  if (types.includes(CrossPieceType.UpperDoorsLowerDoors))
    return CrossPieceType.UpperDoorsLowerDoors;

  throw new Error(
    `Types must be have at least one CrossPieceType: ${types.length} ${types.toString}`,
  );
}

function addOrUpdateCrossPiece(
  add: boolean,
  rackOrTop: Rack,
  position: Position,
  needUpdate: CrossPiecesProperties[],
  width: number,
  material = Material.Wood,
  type: CrossPieceType,
  reinforce = false,
  rod = false,
) {
  if (add)
    rackOrTop.addCrossPiece(position, width, material, type, reinforce, rod);
  else
    rackOrTop.updateCrossPiece(
      position,
      needUpdate.includes(CrossPiecesProperties.Width) ? width : undefined,
      needUpdate.includes(CrossPiecesProperties.Material)
        ? material
        : undefined,
      needUpdate.includes(CrossPiecesProperties.Type) ? type : undefined,
      needUpdate.includes(CrossPiecesProperties.Reinforce)
        ? reinforce
        : undefined,
      needUpdate.includes(CrossPiecesProperties.Rod) ? rod : undefined,
    );
}

function checkAndUpdateCrossPieceType(
  rackOrTop: Rack,
  position: Position,
  shelf: Shelf,
  update: boolean,
) {
  const acceptedTypes =
    rackOrTop instanceof Top
      ? acceptedCrossPieceTypesForTop(shelf, position)
      : acceptedCrossPieceTypesForShelf(shelf, rackOrTop, position);
  const currentCPType = rackOrTop.getCrossPiece(position)?.base.type;
  if (!acceptedTypes.includes(currentCPType)) {
    if (!update) {
      console.debug(
        '[Cross Piece Manager] ERROR type',
        rackOrTop instanceof Top ? 'Top' : `Shelf ${shelf.level}`,
        position,
        currentCPType,
        acceptedTypes,
        shelf,
        rackOrTop,
      );
      throw new Error(
        `${
          rackOrTop instanceof Top
            ? 'Top'
            : `Shelf ${shelf.level} at rack ${rackOrTop.level}`
        } has missing or bad cross piece type for position ${position}`,
      );
    }

    if (acceptedTypes.length === 1 && acceptedTypes[0] === undefined) {
      rackOrTop.removeCrossPiece(position);
      console.debug(
        '[Cross Piece Manager] auto remove CP type',
        rackOrTop instanceof Top
          ? 'Top'
          : `Shelf ${shelf.level} at rack ${rackOrTop.level}`,
        position,
        currentCPType,
      );
    } else {
      addOrUpdateCrossPiece(
        !currentCPType,
        rackOrTop,
        position,
        [CrossPiecesProperties.Type],
        position === Position.Left || position === Position.Right
          ? shelf.cabinet.depth
          : shelf.cabinet.width,
        undefined,
        getDefaultCrossPieceType(acceptedTypes),
      );
      console.debug(
        '[Cross Piece Manager] auto add / update CP type',
        rackOrTop instanceof Top
          ? 'Top'
          : `Shelf ${shelf.level} at rack ${rackOrTop.level}`,
        position,
        currentCPType,
        getDefaultCrossPieceType(acceptedTypes),
      );
    }
  }
}

function checkAndUpdateCrossPiecesMultiRacks(shelf: Shelf, update: boolean) {
  for (const rack of shelf.racks) {
    if (rack.level > 0) {
      for (const cp of rack.crossPieces) {
        // Note: Change here if need multi racks can be reinforced (plates at each rack)
        if (cp.base.reinforce || cp.base.rod) {
          if (!update) {
            console.debug(
              '[Cross Piece Manager] ERROR rod / reinforce inter racks',
              `Shelf ${shelf.level}`,
              cp.position,
              !!cp,
              cp && !!cp.base.rod,
              cp && !!cp.base.reinforce,
              !!shelf.rod,
              shelf.cabinet,
            );
            throw new Error(
              `Shelf ${shelf.level} has inter racks cross piece adapted for rod for position ${cp.position}`,
            );
          }
          rack.updateCrossPiece(
            cp.position as Position,
            undefined,
            undefined,
            undefined,
            false,
            false,
          );
          console.debug(
            '[Cross Piece Manager] auto update CP at rack levels > 0, NOT to hang rod, NOT reinforce',
            `Shelf ${shelf.level} at rack ${rack.level}`,
            cp.position,
            !!cp,
            cp && !!cp.base.rod,
            !!shelf.rod,
            cp && !!cp.base.reinforce,
          );
        }
      }
    }
  }
}

function checkAndUpdateUpperCrossPieceRod(
  rackOrTop: Rack,
  shelf: Shelf,
  position: Position.Left | Position.Right,
  update: boolean,
) {
  const cp = rackOrTop.getCrossPiece(position);

  // Case 1: rod and bad or missing cross piece
  if (shelf.rod && (!cp || (cp && !cp.base.rod))) {
    if (!update) {
      console.debug(
        '[Cross Piece Manager] ERROR rod',
        rackOrTop instanceof Top ? 'Top' : `Shelf ${shelf.level}`,
        position,
        !!cp,
        cp && !!cp.base.rod,
        !!shelf.rod,
        shelf.cabinet,
      );
      throw new Error(
        `Shelf ${shelf.level} has a rod and bad (not adapted) or missing lateral cross piece for position ${position}`,
      );
    }

    addOrUpdateCrossPiece(
      !cp,
      rackOrTop,
      position,
      [CrossPiecesProperties.Rod],
      shelf.cabinet.depth,
      undefined,
      CrossPieceType.UpperEmptyLowerPane,
      false,
      true,
    );

    console.debug(
      '[Cross Piece Manager] auto add / update CP to hang rod',
      rackOrTop instanceof Top ? 'Top' : `Shelf ${shelf.level}`,
      position,
      !!cp,
      cp && !!cp.base.rod,
      !!shelf.rod,
    );
  }
  // Case 2: no rod and bad cross piece
  else if (!shelf.rod && cp && cp.base.rod) {
    if (!update) {
      console.debug(
        '[Cross Piece Manager] ERROR rod',
        rackOrTop instanceof Top ? 'Top' : `Shelf ${shelf.level}`,
        position,
        !!cp,
        cp && !!cp.base.rod,
        !!shelf.rod,
        shelf.cabinet,
      );
      throw new Error(
        `Shelf ${shelf.level} has not a rod and bad (not adapted) lateral cross piece for position ${position}`,
      );
    }

    addOrUpdateCrossPiece(
      false,
      rackOrTop,
      position,
      [CrossPiecesProperties.Rod],
      shelf.cabinet.width,
      undefined,
      CrossPieceType.UpperEmptyLowerPane,
      false,
      false,
    );

    console.debug(
      '[Cross Piece Manager] auto add / update CP, NOT to hang rod',
      rackOrTop instanceof Top ? 'Top' : `Shelf ${shelf.level}`,
      position,
      !!cp,
      cp && !!cp.base.rod,
      !!shelf.rod,
    );
  }
}

function checkAndUpdateLowerCrossPieceRod(
  firstShelf: Shelf,
  position: Position.Left | Position.Right,
  update: boolean,
) {
  const cp = firstShelf.rack(0).getCrossPiece(position);

  if (cp && cp.base.rod) {
    if (!update) {
      console.debug(
        '[Cross Piece Manager] ERROR rod',
        'First shelf',
        position,
        !!cp,
        cp && !!cp.base.rod,
        !!firstShelf.rod,
        firstShelf.cabinet,
      );
      throw new Error(
        `First shelf has a bad (rod) lateral cross piece for position ${position}`,
      );
    }

    addOrUpdateCrossPiece(
      false,
      firstShelf.rack(0),
      position,
      [CrossPiecesProperties.Rod],
      firstShelf.cabinet.depth,
      undefined,
      CrossPieceType.UpperEmptyLowerPane,
      false,
      false,
    );

    console.debug(
      '[Cross Piece Manager] auto add / update CP, NOT to hang rod',
      'First shelf',
      position,
      position,
      !!cp,
      cp && !!cp.base.rod,
      !!firstShelf.rod,
    );
  }
}

function checkAndUpdateCrossPiecesSupportByScrew(shelf: Shelf) {
  if (shelf.cabinet.support === CabinetSupport.ToHangScrew) {
    if (shelf.isFirstShelf()) {
      shelf
        .rack(0)
        .updateCrossPiece(
          Position.Back,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          true,
        );
    }
    if (shelf.isLastShelf()) {
      const upperTop = shelf.cabinet.top;
      upperTop.updateCrossPiece(
        Position.Back,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      );
    }
  }
}

function checkAndUpdateCrossPiecesShelf(shelf: Shelf, update: boolean): void {
  /*
Check and update cross pieces in shelf and just above the shelf
The goal si to detect impossible situations (e.g. door and cross pieces without grooves for doors) and fix it
*/
  if (shelf.isLastShelf()) {
    const upperTop = shelf.cabinet.top;
    checkAndUpdateUpperCrossPieceRod(upperTop, shelf, Position.Left, update);
    checkAndUpdateUpperCrossPieceRod(upperTop, shelf, Position.Right, update);

    for (const position of Object.values(Position))
      checkAndUpdateCrossPieceType(upperTop, position, shelf, update);
  } else {
    const upperRack = shelf.cabinet.shelf(shelf.level + 1).rack(0);
    checkAndUpdateUpperCrossPieceRod(upperRack, shelf, Position.Left, update);
    checkAndUpdateUpperCrossPieceRod(upperRack, shelf, Position.Right, update);
  }

  if (shelf.isFirstShelf()) {
    checkAndUpdateLowerCrossPieceRod(shelf, Position.Left, update);
    checkAndUpdateLowerCrossPieceRod(shelf, Position.Right, update);
  }

  checkAndUpdateCrossPiecesMultiRacks(shelf, update);

  for (const position of Object.values(Position)) {
    for (const rack of shelf.racks)
      checkAndUpdateCrossPieceType(rack, position, shelf, update);
  }

  checkAndUpdateCrossPiecesSupportByScrew(shelf);
}

export function checkAndUpdateCrossPieces(
  cabinet: Cabinet,
  update = true,
): void {
  for (const shelf of cabinet.shelves)
    checkAndUpdateCrossPiecesShelf(shelf, update);
}
